/**
 * Lockstep on the browser side: the commands received from the server, filed by tick, and the tick up to which
 * the match may be simulated. A browser never simulates beyond it, so every browser applies the same commands
 * at the same ticks, whatever the network delays.
 */
import type { Command } from '@invaders/game';

/** Below this backlog of confirmed ticks, one tick per 60 Hz step: the normal pace. */
export const CATCH_UP_FROM = 6;
/** Catching up (a hidden tab, a browser that comes back) keeps this many ticks in hand against jitter. */
export const CATCH_UP_KEEP = 2;
/** At most this many ticks in one step while catching up, so the page stays responsive. */
export const MAX_TICKS_PER_STEP = 400;

const NONE: readonly Command[] = [];

export class Lockstep {
  /** Ticks before this one may be simulated. */
  upTo = 0;
  /** Every command received, in order: the log of the match (for replays). */
  readonly log: Command[] = [];
  private readonly byTick = new Map<number, Command[]>();

  receive(upTo: number, cmds: readonly Command[]): void {
    for (const c of cmds) {
      let list = this.byTick.get(c.tick);
      if (!list) this.byTick.set(c.tick, (list = []));
      list.push(c);
      this.log.push(c);
    }
    if (upTo > this.upTo) this.upTo = upTo;
  }

  /** The commands of `tick`, in the order the server stamped them (once: they are removed). */
  take(tick: number): readonly Command[] {
    const list = this.byTick.get(tick);
    if (!list) return NONE;
    this.byTick.delete(tick);
    return list;
  }

  /** How many ticks to simulate now, at a 60 Hz step, for a match at `stateTick`. */
  ticksToRun(stateTick: number): number {
    const backlog = this.upTo - stateTick;
    if (backlog <= 0) return 0;
    if (backlog < CATCH_UP_FROM) return 1;
    return Math.min(backlog - CATCH_UP_KEEP, MAX_TICKS_PER_STEP);
  }
}
